/*
 * Collab pre-order buy box (sections/collab-preorder-pdp.liquid)
 *
 * Liquid renders every option panel (gallery + heading), the selector tiles
 * and one quantity card per item block. This element only:
 *   - switches the visible option when a selector tile is picked
 *   - runs the steppers (full set: each card 0+, preset from settings;
 *     single item: 1+)
 *   - keeps the price and button label in sync, and previews the applied
 *     discount code (cart code or discount_code cookie) when there is one
 *   - previews the optional bundle deal (data-deal-percent): on the full
 *     set with every item at 1 or more, that percent comes off each item
 *     line. The deal is a product discount and Cheeky's codes are order
 *     discounts, so Shopify takes the deal first and the code off the rest
 *   - adds the item products to the cart (never the bundle product)
 *   - locks the buy button when the countdown reaches zero
 * No Shadow DOM, so section styles cascade normally.
 */
if (!customElements.get('collab-preorder')) {
  class CollabPreorder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-cpo-config]');
      try {
        this.config = JSON.parse(configEl ? configEl.textContent : '{}');
      } catch (err) {
        console.error('[collab-preorder] Unreadable config JSON', err);
        return;
      }
      this.items = this.config.items || {};
      this.closed = this.dataset.closed === 'true';

      this.tiles = Array.from(this.querySelectorAll('[data-cpo-option]'));
      this.panels = Array.from(this.querySelectorAll('[data-cpo-option-panel]'));
      this.cards = Array.from(this.querySelectorAll('[data-cpo-card]'));
      this.priceEl = this.querySelector('[data-cpo-price]');
      this.submitEl = this.querySelector('[data-cpo-submit]');
      this.errorEl = this.querySelector('[data-cpo-error]');
      this.cardsHeading = this.querySelector('[data-cpo-cards-heading]');
      this.oldPriceEl = this.querySelector('[data-cpo-price-old]');
      this.codeTagEl = this.querySelector('[data-cpo-code-tag]');
      this.savingsEl = this.querySelector('[data-cpo-savings]');
      this.dealEl = this.querySelector('[data-cpo-deal]');
      this.dealPercent = Number(this.dataset.dealPercent) || 0;

      this.code = this.appliedCode();
      this.codePercent = Number(this.dataset.codePercent) || 0;

      // Full-set quantities persist while the shopper looks at single items.
      this.bundleQty = {};
      this.singleQty = {};
      Object.keys(this.items).forEach((key) => {
        const preset = Number(this.items[key].bundleQty);
        this.bundleQty[key] = this.items[key].available ? (Number.isFinite(preset) ? preset : 1) : 0;
        this.singleQty[key] = 1;
      });

      this.option = this.dataset.initial || 'bundle';

      this.tiles.forEach((tile) => {
        tile.addEventListener('click', () => this.select(tile.dataset.cpoOption));
      });
      this.addEventListener('keydown', (event) => this.onTileKeydown(event));

      this.cards.forEach((card) => {
        const key = card.dataset.cpoCard;
        const minus = card.querySelector('[data-cpo-minus]');
        const plus = card.querySelector('[data-cpo-plus]');
        if (minus) minus.addEventListener('click', () => this.step(key, -1));
        if (plus) plus.addEventListener('click', () => this.step(key, 1));
      });

      if (this.submitEl) this.submitEl.addEventListener('click', () => this.submit());
      this.addEventListener('sweepstakes-countdown:expired', () => this.close());

      this.render();
    }

    /* ----- state ----- */

    // The code attached to the cart (rendered by Liquid), else the
    // discount_code cookie set by creator and campaign links.
    appliedCode() {
      if (this.dataset.code) return this.dataset.code.toUpperCase();
      const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
      if (!match) return '';
      try {
        return decodeURIComponent(match[1]).toUpperCase();
      } catch (err) {
        console.error('[collab-preorder] Unreadable discount_code cookie', err);
        return '';
      }
    }

    // Shopify rounds a code's allocation once per cart line, so the preview
    // does the same rather than taking a percentage of the total.
    // With the bundle deal, the code comes off each line after the deal
    // (lineDeals: cents taken off each line by the deal).
    codeSavings(qty, lineDeals = {}) {
      if (!this.code || this.codePercent <= 0) return 0;
      return Object.keys(qty).reduce((sum, key) => {
        const item = this.items[key];
        if (!item || !item.available || !qty[key]) return sum;
        const lineTotal = item.price * qty[key] - (lineDeals[key] || 0);
        return sum + Math.round((lineTotal * this.codePercent) / 100);
      }, 0);
    }

    // The bundle deal needs the full set with one or more of every item. A
    // sold-out item means the deal cannot be met.
    dealEligible(qty) {
      if (!(this.dealPercent > 0) || this.option !== 'bundle') return false;
      const keys = Object.keys(this.items);
      return keys.length > 0 && keys.every((key) => this.items[key].available && (qty[key] || 0) >= 1);
    }

    // The deal is a product discount: the percent comes off each item line
    // (rounded per line, like Shopify). Returns cents per item key.
    dealLineSavings(qty) {
      if (!this.dealEligible(qty)) return {};
      const lines = {};
      Object.keys(qty).forEach((key) => {
        const item = this.items[key];
        if (!item || !item.available || !qty[key]) return;
        lines[key] = Math.round((item.price * qty[key] * this.dealPercent) / 100);
      });
      return lines;
    }

    quantities() {
      return this.option === 'bundle' ? this.bundleQty : { [this.option]: this.singleQty[this.option] };
    }

    select(option) {
      if (!option || option === this.option) return;
      if (option !== 'bundle' && !this.items[option]) return;
      this.option = option;
      this.hideError();
      this.render();
    }

    step(key, delta) {
      const item = this.items[key];
      if (!item || !item.available || this.closed) return;
      const store = this.option === 'bundle' ? this.bundleQty : this.singleQty;
      const min = this.option === 'bundle' ? 0 : 1;
      store[key] = Math.max(min, (store[key] || 0) + delta);
      this.hideError();
      this.render();
    }

    close() {
      this.closed = true;
      this.render();
    }

    /* ----- view ----- */

    render() {
      const isBundle = this.option === 'bundle';
      const qty = this.quantities();

      this.tiles.forEach((tile) => {
        const current = tile.dataset.cpoOption === this.option;
        tile.classList.toggle('is-current', current);
        tile.setAttribute('aria-checked', current ? 'true' : 'false');
        tile.tabIndex = current ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.cpoOptionPanel !== this.option;
      });

      const min = isBundle ? 0 : 1;
      this.cards.forEach((card) => {
        const key = card.dataset.cpoCard;
        const visible = isBundle || key === this.option;
        card.hidden = !visible;
        if (!visible) return;
        const count = qty[key] || 0;
        const countEl = card.querySelector('[data-cpo-count]');
        if (countEl) countEl.textContent = String(count);
        card.classList.toggle('is-picked', count > 0);
        const minus = card.querySelector('[data-cpo-minus]');
        const plus = card.querySelector('[data-cpo-plus]');
        if (minus) minus.disabled = this.closed || count <= min;
        if (plus) plus.disabled = this.closed;
      });

      if (this.cardsHeading) this.cardsHeading.textContent = isBundle ? this.dataset.cardsHeading : 'Quantity';

      const total = Object.keys(qty).reduce((sum, key) => {
        const item = this.items[key];
        return item && item.available ? sum + item.price * qty[key] : sum;
      }, 0);
      const lineDeals = this.dealLineSavings(qty);
      const dealSaved = Object.values(lineDeals).reduce((sum, cents) => sum + cents, 0);
      const codeSaved = this.codeSavings(qty, lineDeals);
      const savings = codeSaved + dealSaved;
      const charged = total - savings;
      if (this.priceEl) this.priceEl.textContent = this.formatMoney(charged);
      const showCode = savings > 0;
      if (this.oldPriceEl) {
        this.oldPriceEl.hidden = !showCode;
        this.oldPriceEl.textContent = showCode ? this.formatMoney(total) : '';
      }
      if (this.codeTagEl) {
        this.codeTagEl.hidden = !this.code;
        this.codeTagEl.textContent = this.code ? (this.dataset.codeLabel + ' ' + this.code).trim() : '';
      }
      if (this.savingsEl) {
        this.savingsEl.hidden = !showCode;
        this.savingsEl.textContent = showCode ? 'You save ' + this.formatMoney(savings) : '';
      }
      if (this.dealEl) {
        const applied = dealSaved > 0;
        this.dealEl.classList.toggle('is-applied', applied);
        this.dealEl.textContent = applied ? this.dataset.dealApplied : this.dataset.dealHint;
      }

      if (this.submitEl) {
        const label = this.closed
          ? this.dataset.closedLabel
          : isBundle
            ? this.dataset.bundleLabel
            : this.dataset.singleLabel;
        this.submitEl.textContent = this.closed || total === 0 ? label : label + ' · ' + this.formatMoney(charged);
        this.submitEl.disabled = this.closed || total === 0;
      }
    }

    // Arrow keys move between selector tiles (radiogroup pattern).
    onTileKeydown(event) {
      const tile = event.target.closest('[data-cpo-option]');
      if (!tile) return;
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(event.key in keys)) return;
      event.preventDefault();
      const index = this.tiles.indexOf(tile);
      const next = this.tiles[(index + keys[event.key] + this.tiles.length) % this.tiles.length];
      this.select(next.dataset.cpoOption);
      next.focus();
    }

    // Mirrors Liquid's money_without_trailing_zeros for the shop's money format.
    formatMoney(cents) {
      const format = this.config.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const [whole, decimals] = amount.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const value = decimals === '00' ? withCommas : withCommas + '.' + decimals;
      return format.replace(/\{\{\s*(amount|amount_no_decimals)\s*\}\}/, value).replace(/<[^>]*>/g, '');
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* ----- cart ----- */

    // Section HTML by ID from the Section Rendering API, or null on failure.
    fetchSections(ids) {
      return fetch(window.Shopify.routes.root + '?sections=' + ids.map(encodeURIComponent).join(','))
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null);
    }

    submit() {
      if (this.closed || !this.submitEl || this.submitEl.disabled) return;
      const qty = this.quantities();
      const note = this.dataset.lineNote;
      const items = Object.keys(qty)
        .filter((key) => this.items[key] && this.items[key].available && qty[key] > 0)
        .map((key) => {
          const line = { id: this.items[key].variantId, quantity: qty[key] };
          if (note) line.properties = { 'Pre-order': note };
          return line;
        });
      if (items.length === 0) return;

      const cartDrawer = document.querySelector('cart-drawer');
      const useDrawer =
        this.dataset.afterAdd !== 'cart' && cartDrawer && typeof cartDrawer.renderContents === 'function';
      const payload = { items };
      if (useDrawer) {
        payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const label = this.submitEl.textContent;
      this.submitEl.disabled = true;
      this.submitEl.setAttribute('aria-busy', 'true');
      this.submitEl.textContent = 'Adding…';
      this.hideError();

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((response) =>
          response.json().then((data) => {
            if (!response.ok) {
              throw new Error((data && (data.description || data.message)) || 'Cart add failed (' + response.status + ')');
            }
            return data;
          })
        )
        .then((state) => {
          // constants.js declares PUB_SUB_EVENTS with const, so it is a global
          // binding but not a window property.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'collab-preorder', cartData: state });
          }
          if (useDrawer && state.sections) {
            cartDrawer.renderContents(state);
            return;
          }
          if (useDrawer) {
            // Shopify sends sections: null when it cannot render sections_url,
            // e.g. a page reached through a redirect from an old product
            // handle. Fetch the drawer sections from the home page instead.
            return this.fetchSections(payload.sections).then((sections) => {
              if (sections) {
                cartDrawer.renderContents(Object.assign({}, state, { sections }));
                return;
              }
              window.location.href = window.Shopify.routes.root + 'cart';
            });
          }
          window.location.href = window.Shopify.routes.root + 'cart';
        })
        .catch((err) => {
          console.error('[collab-preorder] add to cart failed', err);
          this.showError(err.message || 'Something went wrong adding this to your cart. Please try again.');
        })
        .finally(() => {
          this.submitEl.removeAttribute('aria-busy');
          this.submitEl.textContent = label;
          this.render();
        });
    }
  }

  customElements.define('collab-preorder', CollabPreorder);
}
